import {useEffect, useRef} from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import busImg from '../../asset/images/Bus.png'

function MultiBusMap({center, schoolLocation, busGeoJson, onMarkerClick}) {
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const busMarkersRef = useRef({}) // Store bus markers by bus number

  /**
   * Initializes the Mapbox map.
   */
  const initializeMap = () => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: [center.lng, center.lat],
      zoom: 16.5,
      maxZoom: 19,
      pitch: 60,
      attributionControl: false,
    })

    return map
  }

  /**
   * Creates a popup element for a marker.
   * @param {Object} feature - The GeoJSON feature for the marker.
   * @returns {HTMLElement} The popup content element.
   */
  const createPopupContent = (feature) => {
    const container = document.createElement('div')
    container.classList.add('p-2')

    const heading = document.createElement('h3')
    heading.classList.add('fs-6', 'text-decoration-underline')
    heading.textContent = `${feature.properties.bus_name} - ${feature.properties.bus_registration_number}`

    const paragraph = document.createElement('p')
    paragraph.classList.add('mb-1')
    paragraph.textContent = `Driver - ${feature.properties.driver_name}`

    const more = document.createElement('p')
    more.classList.add('mb-2', 'text-info', 'small', 'p-0', 'btn-sm', 'btn')
    more.textContent = `More Details`
    more.onclick = () => onMarkerClick(feature)

    container.appendChild(heading)
    container.appendChild(paragraph)
    container.appendChild(more)

    return container
  }

  /**
   * Adds a school marker to the map.
   * @param {Object} map - The Mapbox map instance.
   */
  const addSchoolMarker = (map) => {
    const schoolEl = document.createElement('i')
    schoolEl.classList.add('las', 'la-city', 'fs-2hx', 'text-warning', 'bg-white', 'p-2', 'rounded')

    new mapboxgl.Marker(schoolEl).setLngLat([schoolLocation.lng, schoolLocation.lat]).addTo(map)
  }

  /**
   * Adds bus markers to the map.
   * @param {Object} map - The Mapbox map instance.
   */
  const addBusMarkers = (map) => {
    busGeoJson.features.forEach((feature) => {
      const busEl = document.createElement('img')
      busEl.src = busImg
      busEl.style.width = '60px'

      const popup = createPopupContent(feature)

      const busMarker = new mapboxgl.Marker(busEl)
        .setLngLat(feature.geometry.coordinates)
        .setPopup(
          new mapboxgl.Popup({
            offset: 20,
            className: 'rounded font-monospace',
            closeButton: false,
            closeOnMove: true,
          }).setDOMContent(popup)
        )
        .addTo(map)

      busMarkersRef.current[feature.properties.bus_registration_number] = busMarker
    })
  }

  /**
   * Smoothly animates a marker to a new position.
   * @param {Object} marker - The Mapbox marker instance.
   * @param {Array} start - The starting coordinates [lng, lat].
   * @param {Array} end - The target coordinates [lng, lat].
   * @param {number} duration - Animation duration in milliseconds.
   */
  const animateMarker = (marker, start, end, duration) => {
    const startTime = performance.now()

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime
      const progress = Math.min(elapsedTime / duration, 1)

      const lng = start[0] + (end[0] - start[0]) * progress
      const lat = start[1] + (end[1] - start[1]) * progress

      marker.setLngLat([lng, lat])

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }

  /**
   * Updates the positions of bus markers based on new geoJson data.
   */
  const updateMarkers = () => {
    busGeoJson.features.forEach((feature) => {
      const {bus_registration_number} = feature.properties
      const marker = busMarkersRef.current[bus_registration_number]

      if (marker) {
        const currentCoords = marker.getLngLat()
        const newCoords = feature.geometry.coordinates
        animateMarker(marker, [currentCoords.lng, currentCoords.lat], newCoords, 2500)
      }
    })
  }

  useEffect(() => {
    mapRef.current = initializeMap()

    addSchoolMarker(mapRef.current)
    addBusMarkers(mapRef.current)

    return () => {
      mapRef.current?.remove()
    }
  }, [])

  useEffect(() => {
    mapRef.current?.flyTo({center})
  }, [center])

  useEffect(() => {
    updateMarkers()
  }, [busGeoJson])

  return <div id="map-container" ref={mapContainerRef} className="w-100 h-100" />
}

export default MultiBusMap
